import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import {
  FiActivity,
  FiCalendar,
  FiDownload,
  FiArrowLeft,
  FiLayers,
  FiRefreshCw,
  FiSun,
  FiMoon,
} from "react-icons/fi";
import { BsBoxes } from "react-icons/bs";
import { useEnvironment } from "../../Contexts/EnvironmentContext";
import {
  getJobExecutionApiCallSummary,
  AutomationApiCallSummaryRow,
  JobExecutionApiCallSummaryRow,
} from "../../services/Dashboard.service";
import style from "./ApiCallCostSummary.module.scss";

ChartJS.register(
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip
);

// type KpiView = "Overview" | "Cost" | "Usage";
type SortKey = keyof JobExecutionApiCallSummaryRow | "success_rate";
type DashboardTheme = "dark" | "light";
type DashboardMode = "orchestration" | "automation";

const numberFormatter = new Intl.NumberFormat("en-US");
const compactFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const numberValue = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const timestamp = (value?: string | null) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatNumber = (value: number, compact = false) =>
  (compact ? compactFormatter : numberFormatter).format(Math.round(value));

const formatDecimal = (value: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);

const monthLabel = (row: JobExecutionApiCallSummaryRow | AutomationApiCallSummaryRow) => {
  if (row.activity_month_label) return row.activity_month_label;
  if (row.activity_month_start_date) {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      year: "numeric",
    }).format(new Date(row.activity_month_start_date));
  }
  return row.activity_year && row.activity_month
    ? `${row.activity_year}-${String(row.activity_month).padStart(2, "0")}`
    : "Unknown";
};

const successRate = (row: JobExecutionApiCallSummaryRow) => {
  const runs = numberValue(row.task_pipeline_run_count);
  return runs ? (numberValue(row.succeeded_count) / runs) * 100 : 0;
};

const csvCell = (value: unknown) =>
  `"${String(value ?? "").replace(/"/g, '""')}"`;

const exportCsv = (rows: JobExecutionApiCallSummaryRow[], environmentName: string) => {
  const headers = [
    "Month", "Environment Name", "Grouping Path", "Pipeline Runs",
    "API Calls per Run", "Total API Calls", "Job Executions",
    "Unique Jobs", "Unique Task Groups", "Unique Tasks", "Succeeded Count",
    "Success Rate", "First Activity", "Last Activity", "First Task Start",
    "Last Task End", "First Log Created", "Last Log Updated",
  ];
  const body = rows.map((row) => [
    monthLabel(row), environmentName, row.grouping_path,
    row.task_pipeline_run_count, row.api_calls_per_task_pipeline_run,
    row.estimated_api_call_count, row.job_execution_count, row.unique_job_count,
    row.unique_job_task_group_count, row.unique_job_task_count,
    row.succeeded_count,
    `${successRate(row).toFixed(2)}%`, row.first_activity_datetime,
    row.last_activity_datetime, row.first_task_start_time, row.last_task_end_time,
    row.first_log_created_at, row.last_log_updated_at,
  ].map(csvCell).join(","));
  const blob = new Blob([[headers.map(csvCell).join(","), ...body].join("\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "monthly-api-usage-execution-summary.csv";
  anchor.click();
  URL.revokeObjectURL(url);
};

const exportAutomationCsv = (rows: AutomationApiCallSummaryRow[], environmentName: string) => {
  const headers = ["Month", "Environment Name", "Total Automations", "Data Vault Automations", "Data Mart Automations"];
  const body = rows.map((row) => [monthLabel(row), environmentName, row.total_automation_count, row.total_data_vault_automations, row.total_data_mart_automations].map(csvCell).join(","));
  const blob = new Blob([[headers.map(csvCell).join(","), ...body].join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "automation-api-usage-summary.csv";
  anchor.click();
  URL.revokeObjectURL(url);
};

const Sparkline = ({ values, color }: { values: number[]; color: string }) => {
  const points = useMemo(() => {
    const max = Math.max(...values, 1);
    const min = Math.min(...values, 0);
    const spread = Math.max(max - min, 1);
    return values.map((value, index) => {
      const x = values.length < 2 ? 50 : (index / (values.length - 1)) * 100;
      const y = 25 - ((value - min) / spread) * 18;
      return `${x},${y}`;
    }).join(" ");
  }, [values]);
  return (
    <svg className={style.sparkline} viewBox="0 0 100 30" preserveAspectRatio="none" aria-hidden>
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" />
      {points && <circle cx={points.split(" ").at(-1)?.split(",")[0]} cy={points.split(" ").at(-1)?.split(",")[1]} r="1.8" fill={color} />}
    </svg>
  );
};

const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`${style.skeleton} ${className}`} />
);

const GovernanceDashboard = ({
  rows, loading, sortKey, sortDirection, sortBy, environmentName, isLight,
}: {
  rows: AutomationApiCallSummaryRow[]; loading: boolean;
  sortKey: "activity_month_start_date" | "total_automation_count"; sortDirection: "asc" | "desc"; sortBy: (key: "activity_month_start_date" | "total_automation_count") => void;
  environmentName: string;
  isLight: boolean;
}) => {
  const totalAutomations = rows.reduce((sum, row) => sum + numberValue(row.total_automation_count), 0);
  const dataMartCount = rows.reduce((sum, row) => sum + numberValue(row.total_data_mart_automations), 0);
  const dataVaultCount = rows.reduce((sum, row) => sum + numberValue(row.total_data_vault_automations), 0);
  const stats = [["Total Automations", totalAutomations], ["Data Mart Automations", dataMartCount], ["Data Vault Automations", dataVaultCount]];
  const chartRows = [...rows].sort((a, b) => timestamp(a.activity_month_start_date) - timestamp(b.activity_month_start_date));
  const chartData = {
    labels: chartRows.map(monthLabel),
    datasets: [
      { label: "Data Vault Automations", data: chartRows.map((row) => numberValue(row.total_data_vault_automations)), borderColor: "#16d6d2", backgroundColor: "rgba(22, 214, 210, .13)", pointBackgroundColor: "#16d6d2", tension: 0.3, fill: true },
      { label: "Data Mart Automations", data: chartRows.map((row) => numberValue(row.total_data_mart_automations)), borderColor: "#a76cff", backgroundColor: "transparent", pointBackgroundColor: "#a76cff", tension: 0.3, fill: false },
    ],
  };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index" as const, intersect: false },
    plugins: {
      legend: { align: "start" as const, labels: { color: isLight ? "#24364a" : "#c9d7e7", usePointStyle: true, boxWidth: 7, padding: 20 } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: isLight ? "#58708a" : "#8799ad", maxRotation: 0, autoSkipPadding: 24 }, border: { color: isLight ? "rgba(80, 111, 143, .3)" : "rgba(111, 147, 184, .2)" } },
      y: { beginAtZero: true, grid: { color: isLight ? "rgba(80, 111, 143, .16)" : "rgba(107, 142, 176, .12)" }, ticks: { color: isLight ? "#58708a" : "#8799ad", callback: (value: string | number) => formatNumber(Number(value), true) }, title: { display: true, text: "Automations", color: isLight ? "#58708a" : "#8799ad" }, border: { display: false } },
    },
  };
  return <div className={style.modeContent}>
    <section className={style.governanceStats} aria-label="Automation summary">
      {[<FiLayers />, <FiActivity />, <BsBoxes />].map((icon, index) => {
        const values = [totalAutomations, dataMartCount, dataVaultCount];
        const percentage = totalAutomations ? (values[index] / totalAutomations) * 100 : 0;
        return <article className={style.kpiCard} key={index} style={{ "--accent": ["#1f8fff", "#a76cff", "#16d6d2"][index] } as React.CSSProperties}>
          <div className={style.kpiHeading}><span>{icon}</span><h2>{stats[index][0]}</h2></div>
          <strong>{formatNumber(values[index])}</strong>
          <div className={style.kpiMeter} aria-label={`${percentage.toFixed(1)} percent of automation records`}><span style={{ width: `${percentage}%` }} /></div>
          <small className={style.kpiPercentage}>{percentage.toFixed(1)}% of records</small>
        </article>;
      })}
    </section>
    <section className={`${style.panel} ${style.trendPanel}`}>
      <div className={style.panelHeader}><div><b>2</b><h2>Monthly Trend: Data Vault &amp; Data Mart Automations</h2></div></div>
      <div className={style.chart}>
        {loading ? <Skeleton className={style.chartSkeleton} /> : rows.length ? <Line data={chartData} options={chartOptions} /> : <div className={style.empty}>No automation records found for selected filters.</div>}
      </div>
    </section>
    <section className={`${style.panel} ${style.tablePanel} ${style.automationTablePanel}`}>
      <div className={style.panelHeader}><div><b>1</b><h2>Monthly Automation Summary</h2></div><div className={style.tableTools}><button onClick={() => exportAutomationCsv(rows, environmentName)} disabled={!rows.length}><FiDownload /> Download CSV</button></div></div>
      <div className={style.tableWrap}><table className={style.automationSummaryTable}><colgroup><col /><col /><col /><col /><col /></colgroup><thead><tr><th><button onClick={() => sortBy("activity_month_start_date")}>Month{sortKey === "activity_month_start_date" && <span>{sortDirection === "desc" ? "↓" : "↑"}</span>}</button></th><th>Environment Name</th><th><button onClick={() => sortBy("total_automation_count")}>Total Automations{sortKey === "total_automation_count" && <span>{sortDirection === "desc" ? "↓" : "↑"}</span>}</button></th><th>Data Vault Automations</th><th>Data Mart Automations</th></tr></thead><tbody>
        {loading ? Array.from({ length: 6 }, (_, index) => <tr key={index}>{Array.from({ length: 5 }, (_, cell) => <td key={cell}><Skeleton /></td>)}</tr>) : rows.map((row) => <tr key={`${row.activity_month_start_date}-${row.environment_id}`}><td className={style.month}>{monthLabel(row)}</td><td>{environmentName}</td><td className={style.blue}>{formatNumber(numberValue(row.total_automation_count))}</td><td>{formatNumber(numberValue(row.total_data_vault_automations))}</td><td>{formatNumber(numberValue(row.total_data_mart_automations))}</td></tr>)}
        {!loading && !rows.length && <tr><td colSpan={5}><div className={style.empty}>No automation records found for selected filters.</div></td></tr>}
      </tbody></table></div>
      <footer><span>Showing all {rows.length} records</span></footer>
    </section>
  </div>;
};

const ApiUsageDashboard = () => {
  const navigate = useNavigate();
  const { environments } = useEnvironment();
  const storedEnvironmentId = Number(localStorage.getItem("environment_id"));
  const initialEnvironmentId = Number.isFinite(storedEnvironmentId)
    ? storedEnvironmentId : 114;
  const [theme, setTheme] = useState<DashboardTheme>(() =>
    localStorage.getItem("api-summary-theme") === "light" ? "light" : "dark"
  );
  const [environmentId, setEnvironmentId] = useState(initialEnvironmentId);
  const [rows, setRows] = useState<JobExecutionApiCallSummaryRow[]>([]);
  const [automationRows, setAutomationRows] = useState<AutomationApiCallSummaryRow[]>([]);
  const [mode, setMode] = useState<DashboardMode>("orchestration");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [monthFilter, setMonthFilter] = useState("All");
  // const [view, setView] = useState<KpiView>("Overview");
  const [sortKey, setSortKey] = useState<SortKey>("activity_month_start_date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [automationSortKey, setAutomationSortKey] = useState<"activity_month_start_date" | "total_automation_count">("activity_month_start_date");
  const [automationSortDirection, setAutomationSortDirection] = useState<"asc" | "desc">("desc");
  const isLight = theme === "light";
  const environmentName = environments.find((environment) => environment.environment_id === environmentId)?.customer_environment_name ?? `Environment ${environmentId}`;

  useEffect(() => {
    localStorage.setItem("api-summary-theme", theme);
  }, [theme]);

  const loadSummary = useCallback(async () => {
    if (!Number.isFinite(environmentId) || environmentId <= 0) {
      setError(true);
      setRows([]);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const response = await getJobExecutionApiCallSummary({
        grouping_path: ["ENVIRONMENT"],
        api_calls_per_task_pipeline_run: 0,
        start_date: startDate || undefined,
        end_date_exclusive: endDate || undefined,
        consumption_type: mode === "automation" ? 1 : 0,
      }, environmentId);
      if (mode === "automation") {
        setAutomationRows(Array.isArray(response.data) ? response.data as AutomationApiCallSummaryRow[] : []);
        setRows([]);
      } else {
        setRows(Array.isArray(response.data) ? response.data as JobExecutionApiCallSummaryRow[] : []);
        setAutomationRows([]);
      }
    } catch {
      setRows([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [endDate, environmentId, mode, startDate]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const filteredAutomationRows = useMemo(() => automationRows.filter((row) => {
    const rowDate = timestamp(row.activity_month_start_date);
    const afterStart = !startDate || rowDate >= new Date(`${startDate}T00:00:00`).getTime();
    const beforeEnd = !endDate || rowDate <= new Date(`${endDate}T23:59:59`).getTime();
    const correctMonth = monthFilter === "All" || monthLabel(row) === monthFilter;
    return afterStart && beforeEnd && correctMonth;
  }), [automationRows, endDate, monthFilter, startDate]);

  const sortedAutomationRows = useMemo(() => [...filteredAutomationRows].sort((a, b) => {
    const result = automationSortKey === "activity_month_start_date"
      ? timestamp(a.activity_month_start_date) - timestamp(b.activity_month_start_date)
      : numberValue(a.total_automation_count) - numberValue(b.total_automation_count);
    return automationSortDirection === "asc" ? result : -result;
  }), [automationSortDirection, automationSortKey, filteredAutomationRows]);

  const monthOptions = useMemo(() =>
    Array.from(new Set((mode === "automation" ? automationRows : rows).map(monthLabel))).sort(),
  [automationRows, mode, rows]);

  const filteredRows = useMemo(() => rows.filter((row) => {
    const rowDate = timestamp(row.activity_month_start_date);
    const afterStart = !startDate || rowDate >= new Date(`${startDate}T00:00:00`).getTime();
    const beforeEnd = !endDate || rowDate <= new Date(`${endDate}T23:59:59`).getTime();
    const correctMonth = monthFilter === "All" || monthLabel(row) === monthFilter;
    return afterStart && beforeEnd && correctMonth;
  }), [endDate, monthFilter, rows, startDate]);

  const chronologicalRows = useMemo(() => [...filteredRows].sort(
    (a, b) => timestamp(a.activity_month_start_date) - timestamp(b.activity_month_start_date)
  ), [filteredRows]);

  const totals = useMemo(() => {
    const sum = (key: keyof JobExecutionApiCallSummaryRow) =>
      filteredRows.reduce((total, row) => total + numberValue(row[key]), 0);
    const pipelineRuns = sum("task_pipeline_run_count");
    const successful = sum("succeeded_count");
    return {
      totalApiCalls: sum("estimated_api_call_count"),
      pipelineRuns,
      executions: sum("job_execution_count"),
      successful,
      successRate: pipelineRuns ? (successful / pipelineRuns) * 100 : 0,
      uniqueJobs: Math.max(0, ...filteredRows.map((row) => numberValue(row.unique_job_count))),
      uniqueGroups: Math.max(0, ...filteredRows.map((row) => numberValue(row.unique_job_task_group_count))),
    };
  }, [filteredRows]);

  // const latestDataTimestamp = useMemo(() =>
  //   mode === "automation"
  //     ? Math.max(0, ...governanceRows.map((row) => timestamp(row.updated_at)))
  //     : Math.max(0, ...rows.map((row) => Math.max(timestamp(row.last_log_updated_at), timestamp(row.last_activity_datetime)))),
  //   [governanceRows, mode, rows]);

  const sortedRows = useMemo(() => [...filteredRows].sort((a, b) => {
    const aValue = sortKey === "success_rate" ? successRate(a) : a[sortKey];
    const bValue = sortKey === "success_rate" ? successRate(b) : b[sortKey];
    let result: number;
    if (sortKey.includes("date") || sortKey.includes("activity")) {
      result = timestamp(String(aValue ?? "")) - timestamp(String(bValue ?? ""));
    } else if (typeof aValue === "number" || typeof bValue === "number") {
      result = numberValue(aValue) - numberValue(bValue);
    } else {
      result = String(aValue ?? "").localeCompare(String(bValue ?? ""));
    }
    return sortDirection === "asc" ? result : -result;
  }), [filteredRows, sortDirection, sortKey]);

  const sortBy = (key: SortKey) => {
    if (sortKey === key) setSortDirection((current) => current === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDirection("desc");
    }
  };

  const sortAutomationBy = (key: "activity_month_start_date" | "total_automation_count") => {
    if (automationSortKey === key) setAutomationSortDirection((current) => current === "asc" ? "desc" : "asc");
    else {
      setAutomationSortKey(key);
      setAutomationSortDirection("desc");
    }
  };

  const handleModeChange = (nextMode: DashboardMode) => {
    setMode(nextMode);
    setEnvironmentId(initialEnvironmentId);
    setStartDate("");
    setEndDate("");
    setMonthFilter("All");
  };

  const chartData = useMemo(() => ({
    labels: chronologicalRows.map(monthLabel),
    datasets: [
      {
        label: "Total API Calls",
        data: chronologicalRows.map((row) => numberValue(row.estimated_api_call_count)),
        borderColor: "#1f8fff",
        backgroundColor: "rgba(31, 143, 255, .13)",
        pointBackgroundColor: "#1f8fff",
        pointRadius: chronologicalRows.length > 18 ? 2 : 3,
        pointHoverRadius: 5,
        tension: 0.3,
        fill: true,
        yAxisID: "apiCalls",
      },
      {
        label: "Pipeline Runs",
        data: chronologicalRows.map((row) => numberValue(row.task_pipeline_run_count)),
        borderColor: "#16d6d2",
        backgroundColor: "transparent",
        pointBackgroundColor: "#16d6d2",
        pointRadius: chronologicalRows.length > 18 ? 2 : 3,
        pointHoverRadius: 5,
        tension: 0.3,
        yAxisID: "runs",
      },
    ],
  }), [chronologicalRows]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index" as const, intersect: false },
    plugins: {
      legend: {
        align: "start" as const,
        labels: { color: isLight ? "#24364a" : "#c9d7e7", usePointStyle: true, boxWidth: 7, padding: 20 },
      },
      tooltip: {
        backgroundColor: "rgba(6, 20, 34, .98)",
        borderColor: "rgba(94, 137, 178, .45)",
        borderWidth: 1,
        padding: 12,
        callbacks: {
          afterBody: (items: any[]) => {
            const row = chronologicalRows[items[0]?.dataIndex];
            return row ? [
              `Job executions: ${formatNumber(numberValue(row.job_execution_count))}`,
              `Success rate: ${successRate(row).toFixed(2)}%`,
            ] : [];
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: isLight ? "#58708a" : "#8799ad", maxRotation: 0, autoSkipPadding: 24 },
        border: { color: isLight ? "rgba(80, 111, 143, .3)" : "rgba(111, 147, 184, .2)" },
      },
      apiCalls: {
        position: "left" as const,
        beginAtZero: true,
        grid: { color: isLight ? "rgba(80, 111, 143, .16)" : "rgba(107, 142, 176, .12)" },
        ticks: { color: isLight ? "#58708a" : "#8799ad", callback: (value: string | number) => formatNumber(Number(value), true) },
        title: { display: true, text: "Total API Calls", color: isLight ? "#58708a" : "#8799ad" },
        border: { display: false },
      },
      runs: {
        position: "right" as const,
        beginAtZero: true,
        grid: { drawOnChartArea: false },
        ticks: { color: isLight ? "#58708a" : "#8799ad", callback: (value: string | number) => formatNumber(Number(value), true) },
        title: { display: true, text: "Pipeline Runs", color: "#8799ad" },
        border: { display: false },
      },
    },
  }), [chronologicalRows, isLight]);

  const kpis = [
    { label: "Total API Calls", value: formatNumber(totals.totalApiCalls), icon: <FiActivity />, color: "#1f8fff", values: chronologicalRows.map(r => numberValue(r.estimated_api_call_count)) },
  ];

  return (
    <main className={`${style.page} ${isLight ? style.light : ""}`}>
      <header className={style.header}>
        <div>
          <div className={style.pageTitle}>
            <button
              className={style.backButton}
              onClick={() => navigate("/dashboard")}
              aria-label="Back to dashboard"
              title="Back to dashboard"
            >
              <FiArrowLeft />
            </button>
            <h1>WADE - SAAS API Consumption</h1>
          </div>
          <p>High-level overview of API consumption, execution activity, and outcomes.</p>
        </div>
        <div className={style.headerActions}>
          {/* <div className={style.dataAsOf}>
            <span>Data as of</span>
            <strong>{latestDataTimestamp ? formatDate(new Date(latestDataTimestamp).toISOString()) : "Not available"}</strong>
          </div> */}
          <button onClick={loadSummary} disabled={loading}><FiRefreshCw className={loading ? style.spinning : ""} /> Refresh</button>
          <button onClick={() => mode === "automation" ? exportAutomationCsv(filteredAutomationRows, environmentName) : exportCsv(filteredRows, environmentName)} disabled={mode === "automation" ? !filteredAutomationRows.length : !filteredRows.length}><FiDownload /> Export CSV</button>
          <button
            className={style.themeToggle}
            onClick={() => setTheme((current) => current === "dark" ? "light" : "dark")}
            aria-label={`Switch to ${isLight ? "dark" : "light"} theme`}
            aria-pressed={isLight}
          >
            {isLight ? <FiMoon /> : <FiSun />} {isLight ? "Dark" : "Light"}
          </button>
        </div>
      </header>

      <section className={style.filters} aria-label="Dashboard filters">
        <label>View<select value={mode} onChange={(e) => handleModeChange(e.target.value as DashboardMode)}><option value="orchestration">Orchestration</option><option value="automation">Automation</option></select></label>
        <label>Environment<select value={environmentId} onChange={(e) => setEnvironmentId(Number(e.target.value))}>
          {environments.length ? environments.map((environment) => <option key={environment.environment_id} value={environment.environment_id}>{environment.customer_environment_name ?? `Environment ${environment.environment_id}`}</option>) : <option value={environmentId}>{environmentName}</option>}
        </select></label>
        <label>Date range<div className={style.dateFields}><span><FiCalendar /><input aria-label="Start date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></span><i>to</i><span><input aria-label="End date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></span></div></label>
        <label>Month<select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}><option value="All">All Months</option>{monthOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
        {/* <div className={style.viewFilter}><span>KPI View</span><div>{(["Overview", "Cost", "Usage"] as KpiView[]).map(option => <button key={option} className={view === option ? style.active : ""} onClick={() => setView(option)}>{option}</button>)}</div></div> */}
      </section>

      {error ? (
        <section className={style.errorState}>
          <FiActivity /><h2>Failed to fetch job execution API call summary.</h2>
          <p>Check the selected environment and try again.</p>
          <button onClick={loadSummary}><FiRefreshCw /> Retry</button>
        </section>
      ) : (
        mode === "automation" ? <GovernanceDashboard
          rows={sortedAutomationRows}
          loading={loading}
          sortKey={automationSortKey}
          sortDirection={automationSortDirection}
          sortBy={sortAutomationBy}
          environmentName={environmentName}
          isLight={isLight}
        /> : <div className={style.modeContent}>
          <section className={style.kpiGrid} aria-label={` KPIs`}>
            {kpis.map((kpi) => <article className={style.kpiCard} key={kpi.label} style={{ "--accent": kpi.color } as React.CSSProperties}>
              {loading ? <><Skeleton className={style.iconSkeleton} /><Skeleton /><Skeleton /></> : <>
                <div className={style.kpiHeading}><span>{kpi.icon}</span><h2>{kpi.label}</h2></div>
                <strong>{kpi.value}</strong>
                <Sparkline values={kpi.values} color={kpi.color} />
              </>}
            </article>)}
          </section>

          <section className={`${style.panel} ${style.trendPanel}`}>
            <div className={style.panelHeader}><div><b>2</b><h2>Monthly Trend: Total API Calls &amp; Pipeline Runs</h2></div></div>
            <div className={style.chart}>
              {loading ? <Skeleton className={style.chartSkeleton} /> : filteredRows.length ? <Line data={chartData} options={chartOptions} /> : <div className={style.empty}>No API usage records found for selected filters.</div>}
            </div>
          </section>

          <section className={`${style.panel} ${style.tablePanel}`}>
              <div className={style.panelHeader}>
              <div><b>1</b><h2>Monthly Usage &amp; Execution Summary</h2></div>
              <div className={style.tableTools}><button onClick={() => exportCsv(filteredRows, environmentName)} disabled={!filteredRows.length}><FiDownload /> Download CSV</button></div>
            </div>
            <div className={style.tableWrap}>
              <table>
                <thead><tr>
                  {([
                    ["Month", "activity_month_start_date"], ["Environment Name", "environment_id"],
                    ["Pipeline Runs", "task_pipeline_run_count"], ["API Calls per Run", "api_calls_per_task_pipeline_run"],
                    ["Total API Calls", "estimated_api_call_count"],
                  ] as [string, SortKey][]).map(([label, key]) => <th key={key}><button onClick={() => sortBy(key)}>{label}{sortKey === key && <span>{sortDirection === "desc" ? "↓" : "↑"}</span>}</button></th>)}
                </tr></thead>
                <tbody>
                  {loading ? Array.from({ length: 6 }, (_, index) => <tr key={index}>{Array.from({ length: 5 }, (_, cell) => <td key={cell}><Skeleton /></td>)}</tr>) :
                    sortedRows.map((row, index) => <tr key={`${row.activity_month_start_date}-${row.grouping_path}-${index}`}>
                      <td className={style.month}>{monthLabel(row)}</td><td>{environmentName}</td>
                      <td>{formatNumber(numberValue(row.task_pipeline_run_count))}</td><td>{formatDecimal(numberValue(row.api_calls_per_task_pipeline_run))}</td>
                      <td className={style.blue}>{formatNumber(numberValue(row.estimated_api_call_count))}</td>
                    </tr>)}
                  {!loading && !sortedRows.length && <tr><td colSpan={5}><div className={style.empty}>No API usage records found for selected filters.</div></td></tr>}
                </tbody>
              </table>
            </div>
            <footer><span>Showing all {sortedRows.length} records</span></footer>
          </section>
        </div>
      )}
    </main>
  );
};

export default ApiUsageDashboard;
